'use client';
import { Menu } from '@ark-ui/react/menu';
import { Portal } from '@ark-ui/react/portal';
import { css, cva, cx } from '@repo/panda/css';
import type React from 'react';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { Icon, type IconBaseProps } from '../ds/icon';
import type { MaterialSymbolName } from '../types/material-symbol-names';

const ContextMenuContext = createContext<{
    variant: 'dark' | 'light';
}>({
    variant: 'dark',
});

const useContextMenuContext = () => useContext(ContextMenuContext);

export interface ContextMenuProps extends Omit<Menu.RootProps, 'children' | 'onSelect'> {
    children: ReactNode;
    trigger?: ReactNode;
    triggerClassName?: string;
    anchorPoint?: { x: number; y: number };
    contentClassName?: string;
    size?: ContextMenuVariants['size'];
    variant?: ContextMenuVariants['variant'];
    closeOnSelect?: boolean;
    onSelect?: (details: Menu.SelectionDetails) => void;
    zIndex?: number;
}

export interface ContextMenuItemProps extends Omit<Menu.ItemProps, 'children' | 'className'> {
    children: ReactNode;
    className?: string;
    destructive?: boolean;
    variant?: 'dark' | 'light';
    leadingIcon?: MaterialSymbolName;
    leadingIconProps?: Partial<IconBaseProps>;
    trailingIcon?: MaterialSymbolName;
    trailingIconProps?: Partial<IconBaseProps>;
    onSelect?: () => void;
    value: string;
    asChild?: boolean;
}

export interface ContextMenuSeparatorProps extends Omit<Menu.SeparatorProps, 'className'> {
    className?: string;
    variant?: 'dark' | 'light';
}

export interface ContextMenuGroupProps extends Omit<Menu.ItemGroupProps, 'children' | 'className'> {
    children: ReactNode;
    label?: string;
    className?: string;
    variant?: 'dark' | 'light';
}

const menuContentStyles = cva({
    base: {
        outline: 'none',
        borderRadius: '12',
        boxShadow: 'lg',
        padding: '8',
        minWidth: '[200px]',
        fontSize: 'md',
        fontWeight: 'medium',
        overflow: 'hidden',
        gap: '0',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '[320px]',
        overflowY: 'auto',
        _focus: {
            outline: 'none',
        },
    },
    variants: {
        size: {
            sm: { minWidth: '[160px]', maxWidth: '[200px]' },
            md: { minWidth: '[200px]', maxWidth: '[320px]' },
            lg: { minWidth: '[240px]', maxWidth: '[400px]' },
        },
        variant: {
            dark: {
                background:
                    '[linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2))]',
                color: 'text.primary',
            },
            light: {
                background: 'blackAlpha.5',
                color: 'wemod.black.80',
            },
        },
    },
    defaultVariants: {
        size: 'md',
        variant: 'dark',
    },
});

const menuItemStyles = cva({
    base: {
        display: 'flex',
        alignItems: 'center',
        padding: '12',
        gap: '12',
        borderRadius: '8',
        cursor: 'pointer',
        transition: 'all',
        fontSize: 'md',
        fontWeight: 'medium',
        background: 'transparent',
        border: 'none',
        width: 'full',
        textAlign: 'left',
        outline: 'none',
        _disabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    variants: {
        variant: {
            dark: {
                color: 'text.primary',
                _hover: {
                    background: 'whiteAlpha.10',
                },
                _focusVisible: {
                    background: 'whiteAlpha.10',
                    outline: 'none',
                },
            },
            light: {
                color: 'wemod.black.80',
                _hover: {
                    background: 'blackAlpha.10',
                },
                _focusVisible: {
                    background: 'blackAlpha.10',
                    outline: 'none',
                },
            },
        },
        destructive: {
            true: {
                color: 'red.300',
                _hover: {
                    background: 'red.900/20',
                    color: 'red.200',
                },
                _focusVisible: {
                    background: 'red.900/20',
                    color: 'red.200',
                },
            },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const menuSeparatorStyles = cva({
    base: {
        height: '[1px]',
        marginTop: '2',
        marginBottom: '2',
    },
    variants: {
        variant: {
            dark: {
                borderColor: 'whiteAlpha.10',
            },
            light: {
                borderColor: 'blackAlpha.10',
            },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const menuGroupStyles = css({
    '& + &': {
        paddingTop: '2',
        marginTop: '2',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: 'whiteAlpha.10',
    },
});

const menuGroupLabelStyles = cva({
    base: {
        padding: '[8px 12px]',
        fontSize: 'sm',
        fontWeight: 'medium',
        textTransform: 'uppercase',
        letterSpacing: '[0.5px]',
    },
    variants: {
        variant: {
            dark: {
                color: 'text.secondary',
            },
            light: {
                color: 'wemod.black.60',
            },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

export type ContextMenuVariants = NonNullable<Parameters<typeof menuContentStyles>[0]>;

export const ContextMenu = ({
    children,
    trigger,
    triggerClassName,
    anchorPoint,
    contentClassName,
    size = 'md',
    variant = 'dark',
    closeOnSelect = true,
    onOpenChange,
    onSelect,
    zIndex = 9999,
    ...arkUIProps
}: ContextMenuProps) => {
    const contentStyle = cx(menuContentStyles({ size, variant }), contentClassName);

    return (
        <Menu.Root
            closeOnSelect={closeOnSelect}
            onSelect={onSelect}
            onOpenChange={onOpenChange}
            {...(anchorPoint
                ? {
                      positioning: {
                          getAnchorRect: () => ({ x: anchorPoint.x, y: anchorPoint.y, width: 0, height: 0 }),
                      },
                  }
                : {})}
            {...arkUIProps}
        >
            {trigger && (
                <Menu.ContextTrigger asChild>
                    <div className={triggerClassName}>{trigger}</div>
                </Menu.ContextTrigger>
            )}
            <Portal>
                <Menu.Positioner style={{ zIndex }}>
                    <Menu.Content
                        className={cx(contentStyle, css({ backdropFilter: '[blur(25px)]' }))}
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                        }}
                        role="menu"
                        aria-label="Context menu"
                        tabIndex={-1}
                    >
                        <ContextMenuContext.Provider value={{ variant }}>{children}</ContextMenuContext.Provider>
                    </Menu.Content>
                </Menu.Positioner>
            </Portal>
        </Menu.Root>
    );
};

export const ContextMenuItem = ({
    children,
    disabled = false,
    className,
    destructive = false,
    variant,
    leadingIcon,
    leadingIconProps,
    trailingIcon,
    trailingIconProps,
    onSelect,
    value,
    asChild = false,
    ...arkUIProps
}: ContextMenuItemProps) => {
    const { variant: contextVariant } = useContextMenuContext();
    const finalVariant = variant || contextVariant;

    return (
        <>
            {asChild ? (
                <Menu.Item value={value} {...arkUIProps} asChild>
                    {children}
                </Menu.Item>
            ) : (
                <Menu.Item
                    disabled={disabled}
                    className={cx(menuItemStyles({ destructive, variant: finalVariant }), className)}
                    onSelect={onSelect}
                    value={value}
                    role="menuitem"
                    aria-label={typeof children === 'string' ? children : undefined}
                    tabIndex={disabled ? -1 : 0}
                    {...arkUIProps}
                >
                    {leadingIcon && <Icon icon={leadingIcon} size="sm" {...leadingIconProps} decorative />}
                    <span className={css({ flex: 'auto' })}>{children}</span>
                    {trailingIcon && <Icon icon={trailingIcon} size="sm" {...trailingIconProps} decorative />}
                </Menu.Item>
            )}
        </>
    );
};

export const ContextMenuSeparator = ({ className, variant, ...arkUIProps }: ContextMenuSeparatorProps) => {
    const { variant: contextVariant } = useContextMenuContext();
    const finalVariant = variant || contextVariant;

    return <Menu.Separator className={cx(menuSeparatorStyles({ variant: finalVariant }), className)} {...arkUIProps} />;
};

export const ContextMenuGroup = ({ children, label, className, variant, ...arkUIProps }: ContextMenuGroupProps) => {
    const { variant: contextVariant } = useContextMenuContext();
    const finalVariant = variant || contextVariant;

    return (
        <Menu.ItemGroup className={cx(menuGroupStyles, className)} {...arkUIProps}>
            {label && (
                <Menu.ItemGroupLabel className={menuGroupLabelStyles({ variant: finalVariant })}>
                    {label}
                </Menu.ItemGroupLabel>
            )}
            {children}
        </Menu.ItemGroup>
    );
};
